import React, { type ComponentProps, forwardRef, useRef } from 'react';
import { StyleSheet, TouchableOpacity, type View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';
import useMergedRef from '@react-hook/merged-ref';

import { webOnlyStyles } from '../../../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardPressable = forwardRef<View, Props>((props, ref) => {
  const { style, disabled, accessibilityLabel, isNestedElementFocused, onPress, ...restProps } =
    props;

  const localRef = useRef<View>(null);
  const mergedRef = useMergedRef(localRef, ref);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableStyles = [
    styles.pressable,
    pressableWebStyles,
    isNestedElementFocused ? undefined : styles.pressableOnTop,
    style,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={mergedRef}
      accessibilityLabel={accessibilityLabel}
      role="button"
      style={pressableStyles}
      disabled={disabled === true}
      activeOpacity={0.7}
      {...restProps}
      onPress={onPress}
    />
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.large,
  },
  pressableOnTop: {
    zIndex: 1,
  },
  pressableHover: {
    backgroundColor: theme.colors.OPACITY.WHITE.DARKEST,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: theme.transitions.presets.colorsAndOpacity,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  style?: ViewStyle;
  accessibilityLabel?: string;
  isNestedElementFocused?: boolean;
} & Omit<ComponentProps<typeof TouchableOpacity>, 'style'>;
